/**
 * Discovery page — ranked SKU explorer with AI insights + brand list management.
 *
 * Table: TanStack Table v8 — column-level filters + pagination.
 * Panel filters: margin, qty, cost range, MSRP range, score floor, MAP flag, category, curation state.
 * All filtering is client-side (instant) — data fetched once per brand/preset change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Filter,
  Info,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  StarOff,
  X,
  Zap,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { cn, formatUsd } from "@/lib/utils";
import { ApiError, BrandListEntry, api } from "@/lib/api";

// ── Types ─────────────────────────────────────────────────────────────────────

interface BrandSummary {
  brand: string;
  total_skus: number;
  in_stock_count: number;
  avg_margin_pct: number;
  avg_score: number;
  top_categories: string[];
}

interface ScoredSku {
  tdsynnex_sku: string;
  mpn: string | null;
  description: string | null;
  brand: string | null;
  category: string | null;
  unspsc_code: string;
  unspsc_segment: string;
  cost: number;
  map_price: number;
  msrp: number;
  qty_on_hand: number;
  in_stock: boolean;
  has_map: boolean;
  curated_state: string;
  curated_id: string | null;
  score: number;
  margin_pct: number;
  availability_score: number;
  demand_proxy: number;
  brand_tier: number;
}

interface AIInsight {
  rating: number;
  reasoning: string;
  keywords: string[];
  price_recommendation: string;
  // Deep mode extras
  seo_title?: string;
  seo_description?: string;
  competitor_notes?: string;
  body_html?: string;
  images?: string[];
  backend?: string;
}

type InsightMode = "quick" | "deep";

interface BrandSearchResult {
  slug: string;
  total: number;
  in_stock: number;
}

type Preset = "profitable" | "low_inventory" | "ready_to_list";
type CuratedFilter = "all" | "uncurated" | "curated";
type BrandTab = "preferred" | "mvp" | "other";
type BulkLogEntry = { sku: string; status: "ok" | "error" | "skipped"; error?: string };

const PRESET_LABELS: Record<Preset, string> = {
  profitable: "Profitable",
  low_inventory: "Low Inventory",
  ready_to_list: "Ready to List",
};

const PRESET_DESCRIPTIONS: Record<Preset, string> = {
  profitable: "Margin ≥ 15% — SKUs where you make money after TD SYNNEX cost.",
  low_inventory: "1–10 units on hand — urgency picks before they sell out.",
  ready_to_list: "Uncurated + in stock — the fastest path from catalog to Shopify.",
};

const SCORE_COMPONENTS = [
  { weight: "40%", name: "Margin", color: "bg-emerald-500", formula: "(MSRP − Cost) ÷ MSRP", desc: "Higher margin = more room to price competitively. Capped at 100%." },
  { weight: "25%", name: "Availability", color: "bg-blue-500", formula: "qty_on_hand ÷ 100, capped at 1.0", desc: "Stock confidence. 100+ units = full marks. Zero stock = zero." },
  { weight: "20%", name: "Demand proxy", color: "bg-amber-500", formula: "(MSRP − MAP) ÷ MSRP · 0.5 when MAP absent", desc: "Wide MAP-to-MSRP spread = room to move volume. Neutral 0.5 when TD SYNNEX doesn't supply MAP." },
  { weight: "15%", name: "Brand tier", color: "bg-purple-500", formula: "Fixed lookup", desc: "Lenovo & Dell = 1.0 · Samsung = 0.95 · Anker = 0.90 · Microsoft = 0.85 · Others = 0.60." },
];

const PREFERRED_BRANDS = ["Dell", "Lenovo", "Samsung", "Anker"];

// ── Primitives ────────────────────────────────────────────────────────────────

function RangeSlider({ label, value, min, max, step, unit, onChange }: {
  label: string; value: number; min: number; max: number; step: number; unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums font-medium text-foreground">
          {value === min ? "Any" : `≥ ${value}${unit}`}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-muted
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3.5
          [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:cursor-pointer"
      />
    </div>
  );
}

function MaxSlider({ label, value, min, max, step, unit, onChange }: {
  label: string; value: number; min: number; max: number; step: number; unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums font-medium text-foreground">
          {value >= max ? "Any" : `≤ ${unit}${value.toLocaleString()}`}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-muted
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3.5
          [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:cursor-pointer"
      />
    </div>
  );
}

function PillToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn(
      "px-2.5 py-1 rounded text-xs border transition-colors select-none",
      active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
             : "border-border/60 text-muted-foreground hover:bg-accent/50",
    )}>
      {label}
    </button>
  );
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground">{pct}%</span>
    </div>
  );
}

function ScoreBreakdown({ sku }: { sku: ScoredSku }) {
  const rows = [
    { label: "Margin (40%)", value: sku.margin_pct / 100, display: `${sku.margin_pct.toFixed(1)}%` },
    { label: "Availability (25%)", value: sku.availability_score, display: `${(sku.availability_score * 100).toFixed(0)}%` },
    { label: "Demand (20%)", value: sku.demand_proxy, display: `${(sku.demand_proxy * 100).toFixed(0)}%` },
    { label: "Brand tier (15%)", value: sku.brand_tier, display: `${(sku.brand_tier * 100).toFixed(0)}%` },
  ];
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <div key={r.label} className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{r.label}</span><span>{r.display}</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, r.value * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CuratedBadge({ state }: { state: string }) {
  const styles: Record<string, string> = {
    published: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    ready: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    draft: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    archived: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    uncurated: "bg-muted/30 text-muted-foreground",
  };
  return (
    <Badge className={cn("text-[10px] py-0 px-1.5", styles[state] ?? "bg-muted/30 text-muted-foreground")}>
      {state}
    </Badge>
  );
}

// ── Column filter input ───────────────────────────────────────────────────────

function ColFilterInput({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      placeholder={placeholder ?? "Filter…"}
      className="mt-1 w-full px-1.5 py-0.5 text-[10px] bg-muted/40 border border-border/40 rounded
        placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-ring
        text-foreground font-normal"
    />
  );
}

function ColStateSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="mt-1 w-full px-1 py-0.5 text-[10px] bg-muted/40 border border-border/40 rounded
        focus:outline-none focus:ring-1 focus:ring-ring text-foreground font-normal"
    >
      <option value="">All</option>
      <option value="uncurated">uncurated</option>
      <option value="draft">draft</option>
      <option value="ready">ready</option>
      <option value="published">published</option>
      <option value="archived">archived</option>
    </select>
  );
}

// ── Category filter picker ────────────────────────────────────────────────────

function CategoryPicker({
  skus,
  selected,
  onChange,
}: {
  skus: ScoredSku[];
  selected: Set<string>;
  onChange: (s: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const cats = useMemo(() => {
    const map = new Map<string, { count: number; segment: string; avgMargin: number; totalMargin: number }>();
    for (const s of skus) {
      if (!s.category) continue;
      const existing = map.get(s.category);
      if (existing) {
        existing.count++;
        existing.totalMargin += s.margin_pct;
        existing.avgMargin = existing.totalMargin / existing.count;
      } else {
        map.set(s.category, { count: 1, segment: s.unspsc_segment, avgMargin: s.margin_pct, totalMargin: s.margin_pct });
      }
    }
    return Array.from(map.entries())
      .map(([code, v]) => ({ code, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [skus]);

  const filtered = q
    ? cats.filter((c) => c.code.includes(q) || c.segment.toLowerCase().includes(q.toLowerCase()))
    : cats;

  const toggle = (code: string) => {
    const n = new Set(selected);
    n.has(code) ? n.delete(code) : n.add(code);
    onChange(n);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={cn(
          "flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border transition-colors",
          selected.size > 0
            ? "bg-blue-500/20 text-blue-400 border-blue-500/40"
            : "border-border/60 text-muted-foreground hover:bg-accent/50",
        )}>
          Categories
          {selected.size > 0 && (
            <span className="bg-blue-500 text-white rounded-full px-1.5 text-[10px] leading-4">
              {selected.size}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-[320px] p-0 overflow-hidden">
        <div className="p-2 border-b border-border/60">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter categories…"
              className="w-full pl-7 pr-3 py-1.5 text-xs bg-muted/30 rounded border border-border/40 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
        {selected.size > 0 && (
          <div className="px-3 py-1.5 border-b border-border/40 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{selected.size} selected</span>
            <button onClick={() => onChange(new Set())} className="text-xs text-rose-400 hover:text-rose-300">Clear</button>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto divide-y divide-border/30">
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">No categories found</div>
          )}
          {filtered.slice(0, 50).map((c) => (
            <label key={c.code} className="flex items-center gap-2.5 px-3 py-2 hover:bg-accent/30 cursor-pointer">
              <Checkbox
                checked={selected.has(c.code)}
                onCheckedChange={() => toggle(c.code)}
                className="shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-mono text-muted-foreground">{c.code}</span>
                  {c.count < 50 && (
                    <span className="text-[9px] px-1 rounded bg-purple-500/20 text-purple-400 border border-purple-500/20">niche</span>
                  )}
                </div>
                {c.segment && <div className="text-xs text-foreground truncate">{c.segment}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs tabular-nums text-muted-foreground">{c.count} SKUs</div>
                <div className="text-[10px] tabular-nums text-emerald-400">{c.avgMargin.toFixed(0)}% margin</div>
              </div>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Scoring criteria flyout ───────────────────────────────────────────────────

function ScoringCriteriaFlyout() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-muted-foreground hover:text-foreground transition-colors" title="How scoring works">
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-[380px] p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60 bg-muted/30">
          <div className="text-sm font-semibold">How Discovery scores SKUs</div>
          <div className="text-xs text-muted-foreground mt-0.5">Composite 0–1 · v1 · fixed business weights</div>
        </div>
        <div className="divide-y divide-border/40">
          {SCORE_COMPONENTS.map((c) => (
            <div key={c.name} className="px-4 py-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <div className={cn("h-2 w-2 rounded-full", c.color)} />
                <span className="text-xs font-medium">{c.name}</span>
                <span className="ml-auto text-xs font-mono text-muted-foreground">{c.weight}</span>
              </div>
              <code className="text-[10px] bg-muted/50 px-1.5 py-0.5 rounded text-muted-foreground block">{c.formula}</code>
              <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-border/60 bg-muted/20 space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Presets</div>
          {(Object.entries(PRESET_DESCRIPTIONS) as [Preset, string][]).map(([p, desc]) => (
            <div key={p} className="flex gap-2 text-xs">
              <span className="font-medium text-amber-400 shrink-0">{PRESET_LABELS[p]}</span>
              <span className="text-muted-foreground">{desc}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Other brands picker ───────────────────────────────────────────────────────

function OtherBrandsPicker({ mvpSlugs, activeBrand, onSelect, onPromoteToMvp }: {
  mvpSlugs: Set<string>; activeBrand: string;
  onSelect: (slug: string) => void; onPromoteToMvp: (slug: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<BrandSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (query: string) => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    try { setResults((await api.brands(query)).items); }
    catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const handleChange = (v: string) => {
    setQ(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 250);
  };

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults([]); }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={cn(
          "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5",
          activeBrand !== "All" && !PREFERRED_BRANDS.map(b => b.toLowerCase()).includes(activeBrand.toLowerCase()) && !mvpSlugs.has(activeBrand.toLowerCase())
            ? "bg-accent text-accent-foreground border-border"
            : "bg-background text-muted-foreground border-border/60 hover:bg-accent/50",
        )}>
          <Search className="h-3.5 w-3.5" />Other Brands
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-[300px] p-0 overflow-hidden">
        <div className="p-2 border-b border-border/60">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={inputRef} value={q} onChange={(e) => handleChange(e.target.value)}
              placeholder="Search brands…"
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-muted/30 rounded border border-border/40 focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />}
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto">
          {results.length === 0 && q.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">Type to search all TD SYNNEX brands</div>
          )}
          {results.length === 0 && q.length > 0 && !searching && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">No brands found</div>
          )}
          {results.map((b) => {
            const isMvp = mvpSlugs.has(b.slug);
            return (
              <div key={b.slug} className="flex items-center gap-2 px-3 py-2 hover:bg-accent/40 transition-colors">
                <button className="flex-1 text-left" onClick={() => { onSelect(b.slug); setOpen(false); }}>
                  <span className="text-sm capitalize">{b.slug}</span>
                  <span className="ml-2 text-xs text-muted-foreground tabular-nums">
                    {b.total.toLocaleString()} SKUs · {b.in_stock.toLocaleString()} in stock
                  </span>
                </button>
                <button onClick={() => onPromoteToMvp(b.slug)}
                  title={isMvp ? "Remove from MVP" : "Add to MVP list"}
                  className={cn("p-1 rounded transition-colors shrink-0",
                    isMvp ? "text-amber-400 hover:text-amber-300" : "text-muted-foreground/40 hover:text-amber-400",
                  )}>
                  <Star className="h-3.5 w-3.5" fill={isMvp ? "currentColor" : "none"} />
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── MVP list manager ──────────────────────────────────────────────────────────

function MvpListManager({ mvpEntries, onRemove }: { mvpEntries: BrandListEntry[]; onRemove: (slug: string) => void }) {
  if (mvpEntries.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border border-amber-500/30 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 transition-colors">
          <Star className="h-3 w-3" fill="currentColor" />MVP List ({mvpEntries.length})
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-[280px] p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60 bg-muted/30">
          <div className="text-sm font-semibold">MVP Brands</div>
          <div className="text-xs text-muted-foreground mt-0.5">Brands promoted for priority attention</div>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-border/30">
          {mvpEntries.map((e) => (
            <div key={e.brand_slug} className="flex items-center gap-2 px-3 py-2.5">
              <div className="flex-1">
                <div className="text-sm capitalize">{e.brand_slug}</div>
                {e.notes && <div className="text-xs text-muted-foreground">{e.notes}</div>}
              </div>
              <button onClick={() => onRemove(e.brand_slug)} title="Remove from MVP"
                className="text-muted-foreground/40 hover:text-rose-400 transition-colors p-1 rounded shrink-0">
                <StarOff className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="px-4 py-2.5 border-t border-border/60 bg-muted/20 text-xs text-muted-foreground">
          Star any brand in the search picker to add it here
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── AI Insight panel ──────────────────────────────────────────────────────────
//
// Two modes (dual button):
//   Quick  — POST /api/analytics/ai-insight → structured JSON, ~2s, no images
//   Deep   — SSE  /api/insight/{sku}/stream → steps + image search + richer output, ~10s

function AIInsightPanel({ sku, onClose, onDrafted }: {
  sku: ScoredSku | null;
  onClose: () => void;
  onDrafted?: (sku: string, curatedId: string) => void;
}) {
  const navigate = useNavigate();
  const [insight, setInsight] = useState<AIInsight | null>(null);
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftedId, setDraftedId] = useState<string | null>(null);
  const [mode, setMode] = useState<InsightMode>("quick");
  const [steps, setSteps] = useState<string[]>([]);
  const evtRef = useRef<EventSource | null>(null);

  const reset = useCallback(() => {
    setInsight(null); setSteps([]); setLoading(false); setDraftedId(null);
    evtRef.current?.close(); evtRef.current = null;
  }, []);

  useEffect(() => { reset(); }, [sku?.tdsynnex_sku, reset]);

  const handleDraftCurate = useCallback(async () => {
    if (!sku || !insight?.body_html) return;
    setDrafting(true);
    try {
      const title = insight.seo_title || sku.description || sku.tdsynnex_sku;
      const res = await fetch(`/api/curate/${sku.tdsynnex_sku}/commit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          body_html: insight.body_html,
          seo_title: insight.seo_title ?? "",
          seo_description: insight.seo_description ?? "",
          tags: insight.keywords ?? [],
        }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json() as { id: string; state: string };
      setDraftedId(data.id);
      onDrafted?.(sku.tdsynnex_sku, data.id);
      toast.success("Draft created — ready to review", {
        description: title,
        action: { label: "Open", onClick: () => { navigate(`/curated/${data.id}`); onClose(); } },
      });
    } catch (e) {
      toast.error("Draft failed", { description: String(e) });
    } finally {
      setDrafting(false);
    }
  }, [sku, insight, onDrafted]);
  useEffect(() => () => { evtRef.current?.close(); }, []);

  // Quick mode — blocking POST
  const runQuick = useCallback(async () => {
    if (!sku) return;
    setLoading(true); setInsight(null); setSteps([]);
    try {
      const res = await fetch("/api/analytics/ai-insight", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tdsynnex_sku: sku.tdsynnex_sku,
          description: sku.description ?? "",
          brand: sku.brand ?? "",
          margin_pct: sku.margin_pct,
          cost: sku.cost,
          map_price: sku.map_price,
          qty_on_hand: sku.qty_on_hand,
        }),
      });
      if (!res.ok) throw new ApiError(res.status, null, res.statusText);
      setInsight(await res.json());
    } catch (e) {
      toast.error("Quick analysis failed", { description: String(e) });
    } finally {
      setLoading(false);
    }
  }, [sku]);

  // Deep mode — SSE stream
  const runDeep = useCallback(() => {
    if (!sku) return;
    evtRef.current?.close();
    setLoading(true); setInsight(null); setSteps([]);

    const es = new EventSource(`/api/insight/${sku.tdsynnex_sku}/stream`);
    evtRef.current = es;

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as { type: string; text?: string; urls?: string[]; result?: AIInsight };
        if (msg.type === "step" && msg.text) {
          setSteps((s) => [...s, msg.text!]);
        } else if (msg.type === "images_found" && msg.urls) {
          setInsight((prev) => ({ ...(prev ?? { rating: 0, reasoning: "", keywords: [], price_recommendation: "" }), images: msg.urls }));
        } else if (msg.type === "done" && msg.result) {
          setInsight(msg.result);
          setLoading(false);
          es.close();
        } else if (msg.type === "error" && msg.text) {
          toast.error("Deep analysis failed", { description: msg.text });
          setLoading(false);
          es.close();
        }
      } catch { /* ignore malformed */ }
    };
    es.onerror = () => {
      toast.error("SSE connection lost");
      setLoading(false);
      es.close();
    };
  }, [sku]);

  const run = mode === "quick" ? runQuick : runDeep;

  return (
    <Sheet open={!!sku} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[440px] sm:w-[500px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400" />AI Insight
          </SheetTitle>
        </SheetHeader>
        {sku && (
          <div className="mt-6 space-y-6">
            {/* SKU header */}
            <div className="space-y-2">
              <div className="text-sm font-medium">{sku.description ?? sku.tdsynnex_sku}</div>
              <div className="flex gap-2 flex-wrap text-xs text-muted-foreground">
                <span className="font-mono">SKU {sku.tdsynnex_sku}</span>
                {sku.mpn && <span>MPN: {sku.mpn}</span>}
                <span className="capitalize">{sku.brand}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 text-center">
                {[
                  { label: "Cost", value: formatUsd(sku.cost), color: "" },
                  { label: "MAP", value: sku.map_price > 0 ? formatUsd(sku.map_price) : "N/A", color: sku.has_map ? "text-blue-400" : "text-muted-foreground/40" },
                  { label: "MSRP", value: formatUsd(sku.msrp), color: "" },
                  { label: "Margin", value: `${sku.margin_pct.toFixed(1)}%`, color: sku.margin_pct >= 20 ? "text-emerald-400" : sku.margin_pct >= 10 ? "text-amber-400" : "text-rose-400" },
                ].map((f) => (
                  <div key={f.label} className="rounded bg-muted/30 px-2 py-1.5">
                    <div className={cn("text-sm font-semibold tabular-nums", f.color)}>{f.value}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{f.label}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 text-xs flex-wrap">
                {sku.category && <span className="font-mono text-muted-foreground/70 bg-muted/30 px-1.5 py-0.5 rounded">{sku.category}</span>}
                {sku.unspsc_segment && <span className="text-muted-foreground">{sku.unspsc_segment}</span>}
                {sku.unspsc_code && <span className="font-mono text-[10px] text-muted-foreground/50">UNSPSC {sku.unspsc_code}</span>}
                <span className="text-muted-foreground">· {sku.qty_on_hand} in stock</span>
              </div>
            </div>

            {/* Score breakdown */}
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                Score — {Math.round(sku.score * 100)}%
              </div>
              <ScoreBreakdown sku={sku} />
            </div>

            {/* Mode selector + trigger */}
            <div className="space-y-3">
              {/* Mode toggle */}
              <div className="flex rounded-md border border-border/60 overflow-hidden text-xs">
                {(["quick", "deep"] as InsightMode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => { setMode(m); reset(); }}
                    className={cn(
                      "flex-1 py-1.5 px-3 transition-colors font-medium",
                      mode === m
                        ? "bg-amber-500/20 text-amber-400"
                        : "text-muted-foreground hover:bg-accent/50",
                    )}
                  >
                    {m === "quick" ? "⚡ Quick (~2s)" : "🔬 Deep (~10s)"}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground px-0.5">
                {mode === "quick"
                  ? "Fast structured analysis via Claude Haiku. Sell-through rating, keywords, price recommendation."
                  : "Full pipeline: image search + deeper Claude analysis. Includes competitor notes, SEO copy, product images."}
              </div>

              <Button size="sm" onClick={run} disabled={loading} className="w-full">
                {loading ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-2" />}
                {insight ? `Refresh ${mode === "quick" ? "Quick" : "Deep"} Analysis` : `Run ${mode === "quick" ? "Quick" : "Deep"} Analysis`}
              </Button>

              {/* SSE step log (deep mode) */}
              {mode === "deep" && steps.length > 0 && (
                <div className="rounded bg-muted/30 px-3 py-2 space-y-0.5">
                  {steps.map((s, i) => (
                    <div key={i} className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                      <span className="text-emerald-500">✓</span>{s}
                    </div>
                  ))}
                  {loading && (
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />Working…
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Results */}
            {insight && (
              <div className="space-y-4">
                {/* Images (deep only) */}
                {insight.images && insight.images.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Product Images</div>
                    <div className="flex gap-2 flex-wrap">
                      {insight.images.slice(0, 4).map((url, i) => (
                        <img
                          key={i}
                          src={url}
                          alt="product"
                          className="h-20 w-20 object-contain rounded border border-border/40 bg-muted/20"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <div className="text-2xl font-bold text-amber-400">{insight.rating.toFixed(1)}</div>
                  <div className="text-xs text-muted-foreground">/10 sell-through rating</div>
                  {insight.backend && (
                    <span className="ml-auto text-[9px] text-muted-foreground/50 font-mono">{insight.backend}</span>
                  )}
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Analysis</div>
                  <p className="text-sm leading-relaxed">{insight.reasoning}</p>
                </div>

                {insight.price_recommendation && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Price Recommendation</div>
                    <p className="text-sm">{insight.price_recommendation}</p>
                  </div>
                )}

                {insight.competitor_notes && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Competitor Notes</div>
                    <p className="text-sm text-muted-foreground">{insight.competitor_notes}</p>
                  </div>
                )}

                {insight.seo_title && (
                  <div className="rounded bg-muted/30 px-3 py-2 space-y-1">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">SEO</div>
                    <div className="text-xs font-medium">{insight.seo_title}</div>
                    <div className="text-xs text-muted-foreground">{insight.seo_description}</div>
                  </div>
                )}

                {insight.body_html && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Shopify Listing Copy</div>
                    <div
                      className="text-sm prose prose-invert prose-sm max-w-none
                        [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wide [&_h3]:text-muted-foreground [&_h3]:mt-3 [&_h3]:mb-1.5
                        [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-2
                        [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-0.5
                        [&_li]:text-muted-foreground
                        [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse
                        [&_th]:text-left [&_th]:text-[10px] [&_th]:uppercase [&_th]:text-muted-foreground [&_th]:pb-1 [&_th]:border-b [&_th]:border-border/40
                        [&_td]:py-1 [&_td]:border-b [&_td]:border-border/20 [&_td]:text-muted-foreground
                        [&_td:first-child]:font-medium [&_td:first-child]:text-foreground/80"
                      dangerouslySetInnerHTML={{ __html: insight.body_html }}
                    />
                  </div>
                )}

                {insight.keywords && insight.keywords.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Keywords</div>
                    <div className="flex flex-wrap gap-1.5">
                      {insight.keywords.map((kw) => (
                        <Badge key={kw} variant="secondary" className="text-[10px] py-0">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Draft & Curate CTA — shown when deep analysis produced body_html */}
                {insight.body_html && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <Zap className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium">Ready to publish</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Deep analysis generated full listing copy, SEO meta, and keywords.
                          Draft it to Shopify in one click.
                        </div>
                      </div>
                    </div>
                    {draftedId ? (
                      <a
                        href={`/curated/${draftedId}`}
                        className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-medium"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Draft created — open in Curation Editor
                      </a>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                        onClick={handleDraftCurate}
                        disabled={drafting}
                      >
                        {drafting
                          ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Creating draft…</>
                          : <><Sparkles className="h-3.5 w-3.5 mr-2" />Draft &amp; Curate — ready for Shopify</>
                        }
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const MAX_COST = 10000;
const MAX_MSRP = 15000;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function DiscoveryPage() {
  // Remote data
  const [analyticsScores, setAnalyticsScores] = useState<BrandSummary[]>([]);
  const [rawSkus, setRawSkus] = useState<ScoredSku[]>([]);
  const [loading, setLoading] = useState(false);
  const [brandLists, setBrandLists] = useState<{ preferred: BrandListEntry[]; mvp: BrandListEntry[]; excluded: BrandListEntry[] }>({
    preferred: [], mvp: [], excluded: [],
  });

  // Query state (triggers refetch)
  const [brandTab, setBrandTab] = useState<BrandTab>("preferred");
  const [activeBrand, setActiveBrand] = useState<string>("All");
  const [preset, setPreset] = useState<Preset>("profitable");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Panel filters — smart defaults = "best potential"
  const [minMargin, setMinMargin] = useState(15);
  const [minQty, setMinQty] = useState(0);
  const [maxCost, setMaxCost] = useState(MAX_COST);
  const [maxMsrp, setMaxMsrp] = useState(MAX_MSRP);
  const [minScore, setMinScore] = useState(0);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [mapOnly, setMapOnly] = useState(false);
  const [curatedFilter, setCuratedFilter] = useState<CuratedFilter>("uncurated");
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // TanStack table state
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // UI state
  const [drafting, setDrafting] = useState<Set<string>>(new Set());
  const [insightSku, setInsightSku] = useState<ScoredSku | null>(null);

  // Bulk curation state
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);   // 0–100
  const [bulkTotal, setBulkTotal] = useState(0);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkSucceeded, setBulkSucceeded] = useState(0);
  const [bulkFailed, setBulkFailed] = useState(0);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkLog, setBulkLog] = useState<BulkLogEntry[]>([]);
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
  const bulkLogRef = useRef<HTMLDivElement>(null);
  const bulkEsRef = useRef<EventSource | null>(null);

  const qc = useQueryClient();

  const mvpSlugs = useMemo(() => new Set(brandLists.mvp.map((e) => e.brand_slug.toLowerCase())), [brandLists.mvp]);

  // Fetchers
  const fetchBrandLists = useCallback(async () => {
    try { setBrandLists(await api.brandLists()); } catch { /* non-fatal */ }
  }, []);

  const fetchAnalyticsScores = useCallback(async () => {
    try {
      const data: BrandSummary[] = await fetch("/api/analytics/score/brands").then((r) => r.json());
      setAnalyticsScores(data);
    } catch { /* non-fatal */ }
  }, []);

  const fetchSkus = useCallback(async (brand: string, activePreset: Preset, sq: string) => {
    setLoading(true); setRowSelection({});
    try {
      const q = sq ? `&search=${encodeURIComponent(sq)}` : "";
      const url = brand === "All"
        ? `/api/analytics/score/top?limit=500&preset=${activePreset}${q}`
        : `/api/analytics/score/brand/${encodeURIComponent(brand.toLowerCase())}?limit=5000${q}`;
      const data: ScoredSku[] = await fetch(url).then((r) => r.json());
      setRawSkus(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load SKUs", { description: String(e) });
      setRawSkus([]);
    } finally { setLoading(false); }
  }, []);

  // Debounce search input → searchQuery (triggers refetch)
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { void fetchBrandLists(); void fetchAnalyticsScores(); }, [fetchBrandLists, fetchAnalyticsScores]);
  useEffect(() => { void fetchSkus(activeBrand, preset, searchQuery); }, [activeBrand, preset, searchQuery, fetchSkus]);

  // Brand list actions
  const promoteToMvp = useCallback(async (slug: string) => {
    if (mvpSlugs.has(slug)) { await api.removeFromBrandList(slug, "mvp"); toast.success(`${slug} removed from MVP`); }
    else { await api.addToBrandList(slug, "mvp"); toast.success(`${slug} → MVP list`); }
    void fetchBrandLists();
  }, [mvpSlugs, fetchBrandLists]);

  const removeFromMvp = useCallback(async (slug: string) => {
    await api.removeFromBrandList(slug, "mvp");
    toast.success(`${slug} removed from MVP`);
    void fetchBrandLists();
  }, [fetchBrandLists]);

  // selectedSkus — derived from rowSelection; declared here so curateSelected can use it
  const selectedSkus = useMemo(() => Object.keys(rowSelection).filter((k) => rowSelection[k]), [rowSelection]);

  // Clean up SSE on unmount
  useEffect(() => () => { bulkEsRef.current?.close(); }, []);

  const curateSelected = useCallback(async () => {
    if (selectedSkus.length === 0) return;
    // Reset panel state
    setBulkPanelOpen(true);
    setBulkRunning(true);
    setBulkDone(false);
    setBulkError(null);
    setBulkLog([]);
    setBulkProgress(0);
    setBulkTotal(selectedSkus.length);
    setBulkSucceeded(0);
    setBulkFailed(0);

    let runId: string;
    try {
      const res = await fetch("/api/curate/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ skus: selectedSkus, auto_fetch_images: false }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      const data = await res.json() as { run_id: string; total: number };
      runId = data.run_id;
      setBulkTotal(data.total);
    } catch (e) {
      setBulkError(String(e));
      setBulkRunning(false);
      toast.error("Bulk curation failed to start", { description: String(e) });
      return;
    }

    bulkEsRef.current?.close();
    const es = new EventSource(`/api/curate/bulk/stream?run_id=${encodeURIComponent(runId)}`);
    bulkEsRef.current = es;

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as
          | { type: "started"; total: number }
          | { type: "progress"; index: number; total: number; sku: string; status: "ok" | "error" | "skipped"; error?: string }
          | { type: "done"; succeeded: number; failed: number; total: number }
          | { type: "error"; error: string };

        if (msg.type === "started") {
          setBulkTotal(msg.total);
        } else if (msg.type === "progress") {
          setBulkLog((prev) => [...prev, { sku: msg.sku, status: msg.status, error: msg.error }]);
          setBulkProgress(Math.round(((msg.index + 1) / msg.total) * 100));
          // auto-scroll
          setTimeout(() => {
            if (bulkLogRef.current) {
              bulkLogRef.current.scrollTop = bulkLogRef.current.scrollHeight;
            }
          }, 0);
        } else if (msg.type === "done") {
          setBulkSucceeded(msg.succeeded);
          setBulkFailed(msg.failed);
          setBulkProgress(100);
          setBulkRunning(false);
          setBulkDone(true);
          es.close();
          void qc.invalidateQueries({ queryKey: ["discovery"] });
          void qc.invalidateQueries({ queryKey: ["products"] });
          void fetchSkus(activeBrand, preset, searchQuery);
          setRowSelection({});
          toast.success(`Curation complete — ${msg.succeeded} done, ${msg.failed} failed`);
        } else if (msg.type === "error") {
          setBulkError(msg.error);
          setBulkRunning(false);
          es.close();
          toast.error("Bulk curation error", { description: msg.error });
        }
      } catch { /* ignore malformed */ }
    };
    es.onerror = () => {
      setBulkError("SSE connection lost");
      setBulkRunning(false);
      es.close();
    };
  }, [selectedSkus, activeBrand, preset, searchQuery, fetchSkus, qc]);

  const selectBrand = useCallback((slug: string) => {
    setActiveBrand(slug.charAt(0).toUpperCase() + slug.slice(1));
    setBrandTab("other");
  }, []);

  // Panel filter applied to raw data
  const skus = useMemo(() => rawSkus.filter((s) => {
    if (minMargin > 0 && s.margin_pct < minMargin) return false;
    if (minQty > 0 && s.qty_on_hand < minQty) return false;
    if (maxCost < MAX_COST && s.cost > maxCost) return false;
    if (maxMsrp < MAX_MSRP && s.msrp > maxMsrp) return false;
    if (minScore > 0 && s.score < minScore / 100) return false;
    if (inStockOnly && !s.in_stock) return false;
    if (mapOnly && !s.has_map) return false;
    if (curatedFilter === "uncurated" && s.curated_state !== "uncurated") return false;
    if (curatedFilter === "curated" && s.curated_state === "uncurated") return false;
    if (selectedCats.size > 0 && !selectedCats.has(s.category ?? "")) return false;
    return true;
  }), [rawSkus, minMargin, minQty, maxCost, maxMsrp, minScore, inStockOnly, mapOnly, curatedFilter, selectedCats]);

  const isDefaultFilters = useMemo(() =>
    minMargin === 15 && minQty === 0 && maxCost >= MAX_COST && maxMsrp >= MAX_MSRP &&
    minScore === 0 && inStockOnly && !mapOnly && curatedFilter === "uncurated" && selectedCats.size === 0,
    [minMargin, minQty, maxCost, maxMsrp, minScore, inStockOnly, mapOnly, curatedFilter, selectedCats]);

  const activeFilterCount = useMemo(() =>
    [minMargin !== 15, minQty > 0, maxCost < MAX_COST, maxMsrp < MAX_MSRP, minScore > 0,
     !inStockOnly, mapOnly, curatedFilter !== "uncurated", selectedCats.size > 0].filter(Boolean).length,
    [minMargin, minQty, maxCost, maxMsrp, minScore, inStockOnly, mapOnly, curatedFilter, selectedCats]);

  const resetFilters = useCallback(() => {
    setMinMargin(15); setMinQty(0); setMaxCost(MAX_COST); setMaxMsrp(MAX_MSRP);
    setMinScore(0); setInStockOnly(true); setMapOnly(false); setCuratedFilter("uncurated"); setSelectedCats(new Set());
    setColumnFilters([]);
    setSearchInput(""); setSearchQuery("");
  }, []);

  // Draft actions
  const draftSku = useCallback(async (sku: string) => {
    setDrafting((d) => new Set(d).add(sku));
    try {
      const res = await fetch(`/api/curate/${sku}`, { method: "POST" });
      if (!res.ok) throw new ApiError(res.status, null, res.statusText);
      toast.success(`Draft created for ${sku}`);
      void fetchSkus(activeBrand, preset, searchQuery);
    } catch (e) {
      toast.error("Draft failed", { description: String(e) });
    } finally {
      setDrafting((d) => { const n = new Set(d); n.delete(sku); return n; });
    }
  }, [activeBrand, preset, fetchSkus]);

  // Niche category set (computed once from rawSkus)
  const nicheCategories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of rawSkus) {
      if (s.category) counts.set(s.category, (counts.get(s.category) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n < 50).map(([c]) => c));
  }, [rawSkus]);

  // Column definitions
  const columns = useMemo<ColumnDef<ScoredSku>[]>(() => [
    {
      id: "select",
      enableSorting: false,
      enableColumnFilter: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.original.tdsynnex_sku}`}
        />
      ),
      size: 40,
    },
    {
      accessorKey: "score",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Score <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      ),
      cell: ({ getValue }) => <ScoreBar score={getValue<number>()} />,
      enableColumnFilter: false,
      size: 110,
    },
    {
      accessorKey: "tdsynnex_sku",
      header: ({ column }) => (
        <div>
          <button
            className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            SKU <ArrowUpDown className="h-3 w-3 opacity-50" />
          </button>
          <ColFilterInput
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(v) => column.setFilterValue(v)}
            placeholder="SKU…"
          />
        </div>
      ),
      cell: ({ getValue }) => <span className="font-mono text-xs">{getValue<string>()}</span>,
      filterFn: "includesString",
      size: 100,
    },
    {
      accessorKey: "mpn",
      header: ({ column }) => (
        <div>
          <span className="text-xs font-medium">MPN</span>
          <ColFilterInput
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(v) => column.setFilterValue(v)}
            placeholder="MPN…"
          />
        </div>
      ),
      cell: ({ getValue }) => <span className="text-xs text-muted-foreground">{getValue<string | null>() ?? "—"}</span>,
      filterFn: "includesString",
      size: 110,
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <div>
          <span className="text-xs font-medium">Description</span>
          <ColFilterInput
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(v) => column.setFilterValue(v)}
            placeholder="Search…"
          />
        </div>
      ),
      cell: ({ getValue }) => (
        <span className="text-sm line-clamp-2 max-w-[200px] block">{getValue<string | null>() ?? "—"}</span>
      ),
      filterFn: "includesString",
      size: 220,
    },
    {
      accessorKey: "brand",
      header: ({ column }) => (
        <div>
          <button
            className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Brand <ArrowUpDown className="h-3 w-3 opacity-50" />
          </button>
          <ColFilterInput
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(v) => column.setFilterValue(v)}
            placeholder="Brand…"
          />
        </div>
      ),
      cell: ({ getValue }) => {
        const brand = getValue<string | null>() ?? "";
        const isMvp = mvpSlugs.has(brand.toLowerCase());
        return (
          <span className="text-xs capitalize flex items-center gap-1">
            {isMvp && <Star className="h-3 w-3 text-amber-400 shrink-0" fill="currentColor" />}
            {brand || "—"}
          </span>
        );
      },
      filterFn: "includesString",
      size: 100,
    },
    {
      id: "segment",
      accessorKey: "unspsc_segment",
      header: ({ column }) => (
        <div>
          <span className="text-xs font-medium">Segment</span>
          <ColFilterInput
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(v) => column.setFilterValue(v)}
            placeholder="Segment…"
          />
        </div>
      ),
      cell: ({ getValue, row }) => {
        const seg = getValue<string>();
        const isNiche = nicheCategories.has(row.original.category ?? "");
        return (
          <div className="flex items-center gap-1">
            {seg
              ? <span className="text-xs text-muted-foreground truncate max-w-[100px]">{seg}</span>
              : <span className="text-xs text-muted-foreground/40">—</span>
            }
            {isNiche && (
              <span className="text-[9px] px-1 rounded bg-purple-500/20 text-purple-400 border border-purple-500/20 shrink-0">niche</span>
            )}
          </div>
        );
      },
      filterFn: "includesString",
      size: 130,
    },
    {
      accessorKey: "cost",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors w-full justify-end"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Cost <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      ),
      cell: ({ getValue }) => <span className="text-xs tabular-nums text-right block">{formatUsd(getValue<number>())}</span>,
      enableColumnFilter: false,
      size: 80,
    },
    {
      accessorKey: "map_price",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors w-full justify-end"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          MAP <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      ),
      cell: ({ getValue }) => {
        const v = getValue<number>();
        return v > 0
          ? <span className="text-xs tabular-nums text-blue-400 text-right block">{formatUsd(v)}</span>
          : <span className="text-xs opacity-30 text-right block">N/A</span>;
      },
      enableColumnFilter: false,
      size: 80,
    },
    {
      accessorKey: "msrp",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors w-full justify-end"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          MSRP <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      ),
      cell: ({ getValue }) => <span className="text-xs tabular-nums text-muted-foreground text-right block">{formatUsd(getValue<number>())}</span>,
      enableColumnFilter: false,
      size: 80,
    },
    {
      accessorKey: "margin_pct",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors w-full justify-end"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Margin% <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      ),
      cell: ({ getValue }) => {
        const v = getValue<number>();
        return (
          <span className={cn("text-xs font-medium tabular-nums text-right block",
            v >= 20 ? "text-emerald-400" : v >= 10 ? "text-amber-400" : "text-rose-400",
          )}>
            {v.toFixed(1)}%
          </span>
        );
      },
      enableColumnFilter: false,
      size: 80,
    },
    {
      accessorKey: "qty_on_hand",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Stock <ArrowUpDown className="h-3 w-3 opacity-50" />
        </button>
      ),
      cell: ({ getValue, row }) => (
        <span className={cn("text-xs tabular-nums text-center block",
          row.original.in_stock ? "text-emerald-400" : "text-rose-400",
        )}>
          {getValue<number>()}
        </span>
      ),
      enableColumnFilter: false,
      size: 70,
    },
    {
      accessorKey: "curated_state",
      header: ({ column }) => (
        <div>
          <span className="text-xs font-medium">State</span>
          <ColStateSelect
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(v) => column.setFilterValue(v || undefined)}
          />
        </div>
      ),
      cell: ({ getValue }) => <CuratedBadge state={getValue<string>()} />,
      filterFn: "equalsString",
      size: 95,
    },
    {
      id: "actions",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => null,
      cell: ({ row }) => {
        const sku = row.original;
        return (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {sku.curated_state === "uncurated" && (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                disabled={drafting.has(sku.tdsynnex_sku)} onClick={() => void draftSku(sku.tdsynnex_sku)}>
                {drafting.has(sku.tdsynnex_sku) ? <Loader2 className="h-3 w-3 animate-spin" /> : "Draft"}
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setInsightSku(sku)} title="AI insight">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
      size: 90,
    },
  ], [mvpSlugs, nicheCategories, drafting, draftSku]);

  const table = useReactTable({
    data: skus,
    columns,
    state: { columnFilters, sorting, pagination, rowSelection },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.tdsynnex_sku,
  });

  const draftSelected = useCallback(async () => {
    for (const sku of selectedSkus) await draftSku(sku);
    setRowSelection({});
  }, [selectedSkus, draftSku]);

  const scoreMap = new Map(analyticsScores.map((b) => [b.brand.toLowerCase(), b]));

  const BrandButton = ({ name }: { name: string }) => {
    const key = name.toLowerCase();
    const summary = scoreMap.get(key);
    const isActive = name === "All" ? activeBrand === "All" : activeBrand.toLowerCase() === key;
    const isMvp = mvpSlugs.has(key);
    return (
      <button onClick={() => setActiveBrand(name)} className={cn(
        "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5",
        isActive ? "bg-accent text-accent-foreground border-border"
                 : "bg-background text-muted-foreground border-border/60 hover:bg-accent/50",
      )}>
        {isMvp && <Star className="h-3 w-3 text-amber-400 shrink-0" fill="currentColor" />}
        {name}
        {summary && <span className="text-xs text-muted-foreground">({summary.total_skus.toLocaleString()})</span>}
      </button>
    );
  };

  const hasColumnFilters = columnFilters.length > 0;
  const filteredCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-400" />
            Discovery
            <ScoringCriteriaFlyout />
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            AI-ranked SKUs — composite score across margin, availability, demand &amp; brand
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedSkus.length > 0 && (
            <>
              <Button size="sm" onClick={draftSelected} disabled={drafting.size > 0}>
                {drafting.size > 0 && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Draft {selectedSkus.length} selected
              </Button>
              <Button size="sm" variant="ember" onClick={() => void curateSelected()} disabled={bulkRunning}>
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Curate {selectedSkus.length} SKUs
              </Button>
            </>
          )}
          {bulkPanelOpen && !bulkRunning && (
            <Button size="sm" variant="outline" onClick={() => setBulkPanelOpen(true)}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5 text-amber-400" />Curation results
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => void fetchSkus(activeBrand, preset, searchQuery)} disabled={loading}>
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Brand navigation */}
      <div className="space-y-2">
        <div className="flex gap-1 text-xs border-b border-border/40 pb-2">
          {(["preferred", "mvp", "other"] as BrandTab[]).map((tab) => (
            <button key={tab} onClick={() => { setBrandTab(tab); if (tab !== "other") setActiveBrand("All"); }}
              className={cn("px-3 py-1 rounded-t text-xs font-medium transition-colors",
                brandTab === tab ? "text-foreground border-b-2 border-foreground -mb-2 pb-2" : "text-muted-foreground hover:text-foreground",
              )}>
              {tab === "mvp" ? (
                <span className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-amber-400" fill="currentColor" />MVP
                  {brandLists.mvp.length > 0 && <span className="bg-amber-500/20 text-amber-400 rounded-full px-1.5 text-[10px]">{brandLists.mvp.length}</span>}
                </span>
              ) : tab === "preferred" ? "Preferred Vendors" : "Other Brands"}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {brandTab === "preferred" && (
            <><BrandButton name="All" />{PREFERRED_BRANDS.map((b) => <BrandButton key={b} name={b} />)}</>
          )}
          {brandTab === "mvp" && (
            brandLists.mvp.length === 0
              ? <span className="text-xs text-muted-foreground italic px-1">No MVP brands yet — search and star a brand to add it here.</span>
              : <><BrandButton name="All" />{brandLists.mvp.map((e) => <BrandButton key={e.brand_slug} name={e.brand_slug.charAt(0).toUpperCase() + e.brand_slug.slice(1)} />)}</>
          )}
          {brandTab === "other" && (
            <>
              <OtherBrandsPicker mvpSlugs={mvpSlugs} activeBrand={activeBrand} onSelect={selectBrand} onPromoteToMvp={promoteToMvp} />
              {activeBrand !== "All" && !PREFERRED_BRANDS.map(b => b.toLowerCase()).includes(activeBrand.toLowerCase()) && !mvpSlugs.has(activeBrand.toLowerCase()) && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-accent text-sm font-medium border border-border">
                  <span className="capitalize">{activeBrand}</span>
                  <button onClick={() => promoteToMvp(activeBrand.toLowerCase())} title="Add to MVP" className="text-muted-foreground/50 hover:text-amber-400 transition-colors">
                    <Star className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setActiveBrand("All")} className="text-muted-foreground/50 hover:text-foreground transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </>
          )}
          {brandTab === "mvp" && brandLists.mvp.length > 0 && (
            <div className="ml-auto"><MvpListManager mvpEntries={brandLists.mvp} onRemove={removeFromMvp} /></div>
          )}
        </div>
      </div>

      {/* Search box — server-side Polars regex across description, MPN, SKU */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search description, MPN, SKU… (regex OK)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full pl-8 pr-8 py-2 text-sm bg-muted/30 border border-border/60 rounded-lg
            placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {searchInput && (
          <button
            onClick={() => { setSearchInput(""); setSearchQuery(""); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Preset + filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5">
          {activeBrand === "All" ? (
            (Object.keys(PRESET_LABELS) as Preset[]).map((p) => (
              <button key={p} onClick={() => setPreset(p)}
                className={cn("px-2.5 py-1 rounded text-xs border transition-colors",
                  preset === p ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
                              : "border-border/60 text-muted-foreground hover:bg-accent/50",
                )}>
                {PRESET_LABELS[p]}
              </button>
            ))
          ) : (
            <span className="text-xs text-muted-foreground/50 italic px-1 self-center">
              Presets apply in All view
            </span>
          )}
        </div>
        <div className="h-4 w-px bg-border/60" />
        <button onClick={() => setFiltersOpen((o) => !o)}
          className={cn("flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border transition-colors",
            filtersOpen || activeFilterCount > 0
              ? "bg-slate-500/20 text-slate-300 border-slate-500/40"
              : "border-border/60 text-muted-foreground hover:bg-accent/50",
          )}>
          <Filter className="h-3 w-3" />Filters
          {activeFilterCount > 0 && <span className="ml-0.5 bg-slate-500 text-white rounded-full px-1.5 py-0 text-[10px] leading-4">{activeFilterCount}</span>}
        </button>
        <CategoryPicker skus={rawSkus} selected={selectedCats} onChange={setSelectedCats} />
        <button
          onClick={() => setMinMargin(minMargin === 0 ? 15 : 0)}
          className={cn(
            "flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors",
            minMargin === 0
              ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
              : "border-border/60 text-muted-foreground hover:bg-accent/50",
          )}
        >
          {minMargin === 0 ? "✓ All margins" : "Show all margins"}
        </button>
        <button
          onClick={() => setInStockOnly((v) => !v)}
          className={cn(
            "flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors",
            inStockOnly
              ? "bg-blue-500/20 text-blue-400 border-blue-500/40"
              : "border-border/60 text-muted-foreground hover:bg-accent/50",
          )}
        >
          {inStockOnly ? "In stock only" : "Show all stock"}
        </button>
        <button
          onClick={() => setCuratedFilter(curatedFilter === "all" ? "uncurated" : "all")}
          className={cn(
            "flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors",
            curatedFilter !== "all"
              ? "bg-violet-500/20 text-violet-400 border-violet-500/40"
              : "border-border/60 text-muted-foreground hover:bg-accent/50",
          )}
        >
          {curatedFilter !== "all" ? "Not curated" : "All states"}
        </button>
        {(!isDefaultFilters || hasColumnFilters) && (
          <button onClick={resetFilters} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Reset all filters
          </button>
        )}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {loading ? "Loading…" : (
            hasColumnFilters
              ? `${filteredCount.toLocaleString()} of ${skus.length.toLocaleString()} SKUs (col filtered)`
              : `${skus.length.toLocaleString()} of ${rawSkus.length.toLocaleString()} SKUs`
          )}
        </span>
      </div>

      {/* Expanded filter panel */}
      {filtersOpen && (
        <div className="rounded-lg border border-border/60 bg-muted/20 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <RangeSlider label="Min margin" value={minMargin} min={0} max={80} step={5} unit="%" onChange={setMinMargin} />
              </div>
              <button
                onClick={() => setMinMargin(minMargin === 0 ? 15 : 0)}
                className={cn(
                  "self-start text-[10px] px-1.5 py-0.5 rounded border transition-colors",
                  minMargin === 0
                    ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
                    : "border-border/50 text-muted-foreground hover:text-foreground hover:border-border",
                )}
              >
                {minMargin === 0 ? "✓ Showing all margins" : "Show all margins"}
              </button>
            </div>
            <RangeSlider label="Min score" value={minScore} min={0} max={90} step={5} unit="%" onChange={setMinScore} />
            <MaxSlider label="Max cost" value={maxCost} min={0} max={MAX_COST} step={100} unit="$" onChange={setMaxCost} />
            <MaxSlider label="Max MSRP" value={maxMsrp} min={0} max={MAX_MSRP} step={250} unit="$" onChange={setMaxMsrp} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-1">
            <RangeSlider label="Min qty on hand" value={minQty} min={0} max={500} step={10} unit="" onChange={setMinQty} />
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted-foreground">Stock / MAP</span>
              <div className="flex gap-1.5 flex-wrap">
                <PillToggle label="In stock" active={inStockOnly} onClick={() => setInStockOnly((v) => !v)} />
                <PillToggle label="MAP enforced" active={mapOnly} onClick={() => setMapOnly((v) => !v)} />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted-foreground">Curation state</span>
              <div className="flex gap-1.5 flex-wrap">
                {(["all", "uncurated", "curated"] as CuratedFilter[]).map((f) => (
                  <PillToggle key={f} label={f === "all" ? "All" : f === "uncurated" ? "Not curated" : "Curated"}
                    active={curatedFilter === f} onClick={() => setCuratedFilter(f)} />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted-foreground">Niche signal</span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Use the <span className="text-blue-400">Categories</span> picker above to filter by product type. "niche" badge = &lt;50 SKUs in that category.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SKU table */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="bg-muted/30 hover:bg-muted/30">
                  {hg.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className="align-top pb-2 pt-2"
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-12 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading ranked SKUs…
                  </TableCell>
                </TableRow>
              )}
              {!loading && table.getRowModel().rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-12 text-muted-foreground">
                    {rawSkus.length > 0 ? "No SKUs match the active filters — try adjusting or resetting them." : "No SKUs found for this selection."}
                  </TableCell>
                </TableRow>
              )}
              {!loading && table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn("cursor-pointer hover:bg-accent/30 transition-colors", row.getIsSelected() && "bg-accent/20")}
                  onClick={() => setInsightSku(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination bar */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/60 bg-muted/10">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Rows per page</span>
            <select
              value={pagination.pageSize}
              onChange={(e) => {
                setPagination((_p) => ({ pageIndex: 0, pageSize: Number(e.target.value) }));
              }}
              className="text-xs bg-muted/40 border border-border/40 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {filteredCount === 0 ? "0" : `${pagination.pageIndex * pagination.pageSize + 1}–${Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredCount)}`}
              {" of "}{filteredCount.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline" className="h-7 w-7 p-0"
                disabled={!table.getCanPreviousPage()}
                onClick={() => table.previousPage()}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="tabular-nums px-1">
                {table.getState().pagination.pageIndex + 1} / {table.getPageCount() || 1}
              </span>
              <Button
                size="sm" variant="outline" className="h-7 w-7 p-0"
                disabled={!table.getCanNextPage()}
                onClick={() => table.nextPage()}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Brand summary strip */}
      {activeBrand !== "All" && scoreMap.get(activeBrand.toLowerCase()) && (
        <div className="text-xs text-muted-foreground border border-border/60 rounded-md px-4 py-3 bg-muted/20">
          {(() => {
            const s = scoreMap.get(activeBrand.toLowerCase())!;
            return (
              <span>
                <span className="font-medium text-foreground capitalize">{s.brand}</span>
                {" · "}{s.total_skus.toLocaleString()} SKUs · {s.in_stock_count.toLocaleString()} in stock ·
                avg margin {s.avg_margin_pct.toFixed(1)}% · avg score {Math.round(s.avg_score * 100)}%
                {s.top_categories.length > 0 && <> · categories: {s.top_categories.slice(0, 3).join(", ")}</>}
              </span>
            );
          })()}
        </div>
      )}

      <AIInsightPanel
        sku={insightSku}
        onClose={() => setInsightSku(null)}
        onDrafted={(sku) => {
          setRawSkus((prev) => prev.map((s) =>
            s.tdsynnex_sku === sku ? { ...s, curated_state: "wip" } : s
          ));
        }}
      />

      {/* Bulk curation progress panel */}
      <Sheet open={bulkPanelOpen} onOpenChange={(open) => {
        if (!open && bulkRunning) return; // prevent close while running
        setBulkPanelOpen(open);
      }}>
        <SheetContent side="bottom" className="h-[420px] flex flex-col gap-0 p-0">
          <SheetHeader className="px-5 pt-4 pb-3 border-b border-border/60 shrink-0">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-amber-400" />
              {bulkDone ? "Curation complete" : bulkError ? "Curation error" : "Curating SKUs…"}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 flex flex-col gap-3 px-5 py-4 overflow-hidden">
            {/* Progress bar + counter */}
            <div className="flex items-center gap-3 shrink-0">
              <Progress value={bulkProgress} className="flex-1 h-2" />
              <span className="text-xs tabular-nums text-muted-foreground shrink-0">
                {bulkDone
                  ? `${bulkSucceeded} of ${bulkTotal} curated`
                  : `${bulkLog.length} of ${bulkTotal}`}
              </span>
            </div>

            {/* Error state */}
            {bulkError && (
              <div className="rounded bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-rose-400">
                {bulkError}
              </div>
            )}

            {/* Done state summary */}
            {bulkDone && (
              <div className="flex items-center gap-4 text-sm shrink-0">
                <span className="text-emerald-400 font-medium">{bulkSucceeded} succeeded</span>
                {bulkFailed > 0 && <span className="text-rose-400 font-medium">{bulkFailed} failed</span>}
              </div>
            )}

            {/* Log box */}
            <div
              ref={bulkLogRef}
              className="flex-1 overflow-y-auto font-mono text-xs rounded bg-muted/30 border border-border/40 px-3 py-2 space-y-0.5"
            >
              {bulkLog.length === 0 && bulkRunning && (
                <div className="text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />Starting…
                </div>
              )}
              {bulkLog.map((entry, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={cn("shrink-0",
                    entry.status === "ok" ? "text-emerald-400" :
                    entry.status === "skipped" ? "text-amber-400" : "text-rose-400",
                  )}>
                    {entry.status === "ok" ? "✓" : entry.status === "skipped" ? "—" : "✗"}
                  </span>
                  <span className="text-muted-foreground">{entry.sku}</span>
                  {entry.error && (
                    <span className="text-rose-400/70 truncate">{entry.error}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer actions */}
          <div className="px-5 pb-4 shrink-0 flex justify-end gap-2">
            {bulkRunning && (
              <span className="text-xs text-muted-foreground self-center flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />Running — do not close
              </span>
            )}
            {!bulkRunning && (
              <Button size="sm" variant="outline" onClick={() => setBulkPanelOpen(false)}>
                Close
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
